import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Upload, X, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/components/auth-provider";
import { Property, Unit, PropertyStructure } from "@/types/types";
import {
  getPropertyById,
  updateProperty,
  UpdatePropertyData,
} from "@/lib/property-service";
import {
  uploadPropertyImages,
  deletePropertyImage,
} from "@/lib/storage-service";
import { sanitizePropertyData } from "@/lib/sanitize";

const PROPERTY_TYPES = [
  "apartment",
  "flat",
  "duplex",
  "bungalow",
  "detached",
  "semi-detached",
  "terrace",
  "penthouse",
  "studio",
  "mansion",
  "estate-house",
  "self-contain",
  "mini-flat",
  "boys-quarters",
];

const AMENITIES_OPTIONS = [
  "Parking",
  "Pool",
  "Gym",
  "Security",
  "Generator",
  "Water Supply",
  "Air Conditioning",
  "Elevator",
  "Balcony",
  "Garden",
  "Playground",
  "Laundry",
  "CCTV",
  "Gate",
];

export default function PropertyEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [property, setProperty] = useState<Property | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    address: "",
    type: "apartment" as Property["type"],
    structure: "single-unit" as PropertyStructure,
    bedrooms: 1,
    bathrooms: 1,
    area: 0,
    rent: 0,
    description: "",
    amenities: [] as string[],
    imageUrls: [] as string[],
    totalFloors: 1,
    totalUnits: 0,
    buildingFacilities: [] as string[],
    units: [] as Partial<Unit>[],
  });

  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  async function loadProperty() {
    if (!id) return;

    try {
      setLoading(true);
      const propertyData = await getPropertyById(id);
      if (!propertyData) {
        toast.error("Property not found");
        navigate("/landlord/properties");
        return;
      }
      setProperty(propertyData);

      // Populate form with existing data
      setFormData({
        title: propertyData.title || "",
        address: propertyData.address || "",
        type: propertyData.type || "apartment",
        structure: propertyData.structure || "single-unit",
        bedrooms: propertyData.bedrooms || 1,
        bathrooms: propertyData.bathrooms || 1,
        area: propertyData.area || 0,
        rent: propertyData.rent || 0,
        description: propertyData.description || "",
        amenities: propertyData.amenities || [],
        imageUrls: propertyData.imageUrls || [],
        totalFloors: propertyData.totalFloors || 1,
        totalUnits: propertyData.totalUnits || 0,
        buildingFacilities: propertyData.buildingFacilities || [],
        units: propertyData.units || [],
      });
    } catch (error) {
      console.error("Error loading property:", error);
      toast.error("Failed to load property");
      navigate("/landlord/dashboard/properties");
    } finally {
      setLoading(false);
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (!user || !id) return;

    const files = Array.from(e.target.files);

    // Validate file sizes (5MB max)
    const oversizedFiles = files.filter((file) => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`${oversizedFiles.length} file(s) exceed 5MB limit`);
      return;
    }

    try {
      setUploadingImages(true);
      const newImageUrls = await uploadPropertyImages(files, id, user.id);
      setFormData((prev) => ({
        ...prev,
        imageUrls: [...prev.imageUrls, ...newImageUrls],
      }));
      toast.success(`${newImageUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = async (imageUrl: string) => {
    try {
      await deletePropertyImage(imageUrl);
      setFormData((prev) => ({
        ...prev,
        imageUrls: prev.imageUrls.filter((url) => url !== imageUrl),
      }));
      toast.success("Image removed successfully");
    } catch (error) {
      console.error("Error removing image:", error);
      toast.error("Failed to remove image");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    // Validate required fields
    if (
      !formData.title.trim() ||
      !formData.address.trim() ||
      formData.rent <= 0
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);

      const updateData: UpdatePropertyData = {
        title: formData.title,
        address: formData.address,
        type: formData.type,
        structure: formData.structure,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        area: formData.area,
        rent: formData.rent,
        description: formData.description,
        amenities: formData.amenities,
        imageUrls: formData.imageUrls,
        totalFloors: formData.totalFloors,
        totalUnits: formData.totalUnits,
        buildingFacilities: formData.buildingFacilities,
        units: formData.units,
      };

      // Sanitize property data before submission
      const sanitizedData = sanitizePropertyData(updateData);
      await updateProperty(id, sanitizedData);
      toast.success("Property updated successfully");
      navigate(`/landlord/properties/${id}`);
    } catch (error) {
      console.error("Error updating property:", error);
      toast.error("Failed to update property");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAmenity = (amenity: string) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading property...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold">Property Not Found</h2>
          <Button onClick={() => navigate("/landlord/dashboard/properties")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Properties
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/landlord/properties/${id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edit Property</h1>
            <p className="text-muted-foreground mt-1">{property.title}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-3">
          {/* Main Form */}
          <div className="md:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Update the primary details of your property
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Property Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g., Modern 3BR Apartment in Lekki"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    placeholder="e.g., 123 Admiralty Way, Lekki Phase 1, Lagos"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="structure">Property Structure *</Label>
                  <Select
                    value={formData.structure}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        structure: value as PropertyStructure,
                      })
                    }
                  >
                    <SelectTrigger id="structure">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single-unit">Single Unit</SelectItem>
                      <SelectItem value="multi-unit">
                        Multi-Unit Building
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {formData.structure === "single-unit"
                      ? "One property unit (house, duplex, etc.)"
                      : "Multiple units in one building (apartment block)"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Property Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          type: value as Property["type"],
                        })
                      }
                    >
                      <SelectTrigger id="type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.replace("-", " ").charAt(0).toUpperCase() +
                              type.replace("-", " ").slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rent">Monthly Rent (₦) *</Label>
                    <Input
                      id="rent"
                      type="number"
                      min="0"
                      value={formData.rent}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          rent: parseFloat(e.target.value) || 0,
                        })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bedrooms">Bedrooms *</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bedrooms: parseInt(e.target.value) || 0,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bathrooms">Bathrooms *</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      min="0"
                      value={formData.bathrooms}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bathrooms: parseInt(e.target.value) || 0,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="area">Area (m²) *</Label>
                    <Input
                      id="area"
                      type="number"
                      min="0"
                      value={formData.area}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          area: parseFloat(e.target.value) || 0,
                        })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Describe your property, highlight key features..."
                    rows={5}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card>
              <CardHeader>
                <CardTitle>Amenities</CardTitle>
                <CardDescription>
                  Select all amenities available in the property
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {AMENITIES_OPTIONS.map((amenity) => (
                    <div key={amenity} className="flex items-center space-x-2">
                      <Checkbox
                        id={amenity}
                        checked={formData.amenities.includes(amenity)}
                        onCheckedChange={() => toggleAmenity(amenity)}
                      />
                      <label
                        htmlFor={amenity}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {amenity}
                      </label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Multi-Unit Management */}
            {formData.structure === "multi-unit" && (
              <Card>
                <CardHeader>
                  <CardTitle>Units Management</CardTitle>
                  <CardDescription>
                    Add and manage individual units in this building
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="totalFloors">Total Floors</Label>
                      <Input
                        id="totalFloors"
                        type="number"
                        min="1"
                        value={formData.totalFloors}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            totalFloors: parseInt(e.target.value) || 1,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="totalUnits">Total Units</Label>
                      <Input
                        id="totalUnits"
                        type="number"
                        min="0"
                        value={formData.totalUnits}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            totalUnits: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Units ({formData.units.length})</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const newUnit: Partial<Unit> = {
                            id: `unit_${Date.now()}_${Math.random()
                              .toString(36)
                              .substr(2, 9)}`,
                            name: `Unit ${formData.units.length + 1}`,
                            floor: 1,
                            bedrooms: 2,
                            bathrooms: 1,
                            area: 60,
                            rent: formData.rent || 300000,
                            status: "vacant",
                          };
                          setFormData({
                            ...formData,
                            units: [...formData.units, newUnit],
                          });
                        }}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Add Unit
                      </Button>
                    </div>

                    {formData.units.length > 0 ? (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {formData.units.map((unit, index) => (
                          <div
                            key={unit.id || index}
                            className="p-3 border rounded-lg space-y-2"
                          >
                            <div className="flex items-center justify-between">
                              <Input
                                placeholder="Unit name"
                                value={unit.name || ""}
                                onChange={(e) => {
                                  const newUnits = [...formData.units];
                                  newUnits[index] = {
                                    ...newUnits[index],
                                    name: e.target.value,
                                  };
                                  setFormData({ ...formData, units: newUnits });
                                }}
                                className="w-40"
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  const newUnits = formData.units.filter(
                                    (_, i) => i !== index
                                  );
                                  setFormData({ ...formData, units: newUnits });
                                }}
                              >
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-5 gap-2">
                              <Input
                                type="number"
                                placeholder="Floor"
                                value={unit.floor || ""}
                                onChange={(e) => {
                                  const newUnits = [...formData.units];
                                  newUnits[index] = {
                                    ...newUnits[index],
                                    floor: parseInt(e.target.value) || 1,
                                  };
                                  setFormData({ ...formData, units: newUnits });
                                }}
                                className="text-sm"
                              />
                              <Input
                                type="number"
                                placeholder="Beds"
                                value={unit.bedrooms || ""}
                                onChange={(e) => {
                                  const newUnits = [...formData.units];
                                  newUnits[index] = {
                                    ...newUnits[index],
                                    bedrooms: parseInt(e.target.value) || 0,
                                  };
                                  setFormData({ ...formData, units: newUnits });
                                }}
                                className="text-sm"
                              />
                              <Input
                                type="number"
                                placeholder="Baths"
                                value={unit.bathrooms || ""}
                                onChange={(e) => {
                                  const newUnits = [...formData.units];
                                  newUnits[index] = {
                                    ...newUnits[index],
                                    bathrooms: parseInt(e.target.value) || 0,
                                  };
                                  setFormData({ ...formData, units: newUnits });
                                }}
                                className="text-sm"
                              />
                              <Input
                                type="number"
                                placeholder="Area"
                                value={unit.area || ""}
                                onChange={(e) => {
                                  const newUnits = [...formData.units];
                                  newUnits[index] = {
                                    ...newUnits[index],
                                    area: parseFloat(e.target.value) || 0,
                                  };
                                  setFormData({ ...formData, units: newUnits });
                                }}
                                className="text-sm"
                              />
                              <Input
                                type="number"
                                placeholder="Rent"
                                value={unit.rent || ""}
                                onChange={(e) => {
                                  const newUnits = [...formData.units];
                                  newUnits[index] = {
                                    ...newUnits[index],
                                    rent: parseFloat(e.target.value) || 0,
                                  };
                                  setFormData({ ...formData, units: newUnits });
                                }}
                                className="text-sm"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No units added yet. Click "Add Unit" to get started.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Images Sidebar */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle>Property Images</CardTitle>
              <CardDescription>
                Upload high-quality images (max 5MB each)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label
                  htmlFor="images"
                  className="cursor-pointer flex items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg hover:border-muted-foreground/50 transition-colors"
                >
                  {uploadingImages ? (
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <span className="text-sm text-muted-foreground">
                        Uploading...
                      </span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Click to upload images
                      </span>
                    </div>
                  )}
                </Label>
                <Input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImages}
                />
              </div>

              {formData.imageUrls.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Uploaded Images ({formData.imageUrls.length})
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {formData.imageUrls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Property ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(url)}
                          className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {index === 0 && (
                          <div className="absolute bottom-1 left-1 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-md flex items-center gap-1">
                            <Star className="h-3 w-3 fill-current" />
                            Cover
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    First image will be used as the cover photo
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/landlord/properties/${id}`)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || uploadingImages}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
